/**
 * Types and helpers for the GAUTO licence panel.
 *
 * Field names mirror the Worker's schema exactly (snake_case for licences, camelCase payloads) -
 * see gauto/docs/admin-api.md. Do not rename them here; the Worker is the contract.
 */

export interface GautoSummary {
  licences: number;
  active: number;
  revoked: number;
  expired: number;
  expiring_soon: number;
  devices: number;
}

/** A row from `GET /v1/admin/licenses`. */
export interface GautoLicenceListRow {
  email: string;
  expires_at: number;
  status: string;
  max_devices: number;
  note: string | null;
  /** Devices actually used, not the limit. Render as `devices / max_devices`. */
  devices: number;
}

/** A licence as returned by `GET /v1/admin/license?email=`. */
export interface GautoLicence {
  email: string;
  expires_at: number;
  max_devices: number;
  status: string;
  note?: string | null;
}

export interface GautoDevice {
  device_id: string;
  public_key?: string | null;
  first_seen: number;
  last_seen: number;
  renewals: number;
  app_version: string | null;
}

export interface GautoLicenceDetail {
  licence: GautoLicence;
  devices: GautoDevice[];
}

export interface GautoLicencePayload {
  email: string;
  days?: number;
  expiresAt?: number;
  maxDevices?: number;
  status?: 'active' | 'revoked';
  note?: string | null;
}

export interface GautoRenamePayload {
  from: string;
  to: string;
}

export const DAY_SECONDS = 86_400;
/** Tokens are issued with a 30-day life, so "expiring soon" is the same window the summary uses. */
export const EXPIRING_SOON_DAYS = 30;
export const MAX_DEVICES_CEILING = 5;

/** Parsed failure from the API: the Worker's stable codes plus the HTTP status. */
export class GautoApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string) {
    super(code);
    this.name = 'GautoApiError';
    this.status = status;
    this.code = code;
  }
}

const ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'Your admin session expired. Sign in again.',
  not_configured:
    'GAUTO_ADMIN_SECRET is not set for this deployment, so the panel cannot reach the licence service.',
  upstream_unreachable: 'The GAUTO licence service could not be reached.',
  forbidden_route: 'That action is not allowed by the panel.',
  email_required: 'An email address is required.',
  from_and_to_required: 'Both the current and the new email address are required.',
  same_email: 'The new email is the same as the current one.',
  not_found: 'That licence no longer exists. Refresh the list.',
  target_exists: 'Another licence already uses that email address.',
  invalid_body: 'The request body was rejected.',
};

/** Turn an upstream error code into something an operator can act on. */
export function describeError(error: unknown): string {
  if (error instanceof GautoApiError) {
    return ERROR_MESSAGES[error.code] ?? `Request failed (${error.status}: ${error.code}).`;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

export async function gautoRequest<T>(
  url: string,
  init?: { method?: 'GET' | 'POST'; body?: unknown },
): Promise<T> {
  const response = await fetch(url, {
    method: init?.method ?? 'GET',
    headers: init?.body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: init?.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const code =
      (payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
        ? (payload as { error: string }).error
        : undefined) ?? `http_${response.status}`;
    throw new GautoApiError(response.status, code);
  }

  return payload as T;
}

export type LicenceState = 'revoked' | 'expired' | 'expiring' | 'active';

export function licenceState(row: { status: string; expires_at: number }, now = Date.now()): LicenceState {
  if (row.status === 'revoked') return 'revoked';
  if (row.expires_at * 1000 <= now) return 'expired';
  if (row.expires_at * 1000 <= now + EXPIRING_SOON_DAYS * DAY_SECONDS * 1000) return 'expiring';
  return 'active';
}

export function daysUntil(expiresAt: number, now = Date.now()): number {
  return Math.ceil((expiresAt * 1000 - now) / (DAY_SECONDS * 1000));
}

export function formatDate(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return '-';
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export function formatDateTime(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return '-';
  return new Date(unixSeconds * 1000).toLocaleString();
}

/** `2d ago`, `5h ago`, `just now` - device recency reads better than an absolute stamp. */
export function formatRelative(unixSeconds: number | null | undefined, now = Date.now()): string {
  if (!unixSeconds) return '-';
  const diff = now - unixSeconds * 1000;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(unixSeconds);
}

export function shortDeviceId(deviceId: string, length = 12): string {
  return deviceId.length > length ? `${deviceId.slice(0, length)}…` : deviceId;
}

export type ExpiryUnit = 'days' | 'months' | 'years';

/** Unix seconds `amount` units after `from`, so "3 months" lands on the same day of the month. */
export function addTerm(amount: number, unit: ExpiryUnit, from = Date.now()): number {
  const date = new Date(from);
  if (unit === 'days') date.setDate(date.getDate() + amount);
  if (unit === 'months') date.setMonth(date.getMonth() + amount);
  if (unit === 'years') date.setFullYear(date.getFullYear() + amount);
  return Math.floor(date.getTime() / 1000);
}

/**
 * Extend an expiry by `amount` units.
 *
 * Extending from an already-expired licence starts at today, so the tester gains the full term
 * rather than backfilling the gap.
 */
export function extendExpiry(
  expiresAt: number,
  amount: number,
  unit: ExpiryUnit,
  now = Date.now(),
): number {
  const base = Math.max(expiresAt * 1000, now);
  const termSeconds = addTerm(amount, unit, now) - Math.floor(now / 1000);
  return Math.floor(base / 1000) + termSeconds;
}

export function clampMaxDevices(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(Math.max(Math.round(value), 1), MAX_DEVICES_CEILING);
}

/** The fields a caller may override; everything else is carried over from the licence. */
export interface LicenceWriteChanges {
  expiresAt?: number;
  days?: number;
  maxDevices?: number;
  status?: 'active' | 'revoked';
  note?: string | null;
}

/**
 * Build the payload for extending a licence.
 *
 * `POST /v1/admin/license` is an upsert in which every omitted field is reset: sending only
 * `{email, days}` sets `max_devices` back to 1, clears the note and re-activates a blocked licence.
 * So every write from this panel carries the licence's *complete* intended state, and status stays
 * untouched unless the user explicitly blocks or unblocks.
 */
export function buildLicenceWrite(
  licence: GautoLicence,
  changes: LicenceWriteChanges = {},
): GautoLicencePayload {
  return {
    email: licence.email,
    expiresAt: changes.expiresAt,
    days: changes.days,
    maxDevices: changes.maxDevices ?? licence.max_devices,
    status: changes.status ?? (licence.status === 'revoked' ? 'revoked' : 'active'),
    note: changes.note !== undefined ? changes.note : licence.note ?? null,
  };
}
